/**
 * PATH: packages/auth/src/session-manager/deadRefreshRegistry.ts
 * FILE_ID: AUTH-SESSION-DEAD-REFRESH-001
 * PURPOSE: Remember refresh tokens the server has DEFINITIVELY rejected
 *          (401/403) so no refresher ever re-POSTs a token already proven
 *          dead.
 * WHY: BUG-2026-06-29-REFRESH-401-LOOP. A blacklisted/expired 30-day
 *      `quotech_refresh` token lingered in the `.quotech.ai` cookie +
 *      localStorage and was retried for minutes by THREE independent
 *      refreshers (SessionManager 60s tick, the host-app axios 401
 *      interceptor, and apiClient). Prod evidence — repeated
 *      `[refresh-rollover] ... token is blacklisted` (fp 7152e367bf,
 *      user_id=20) and `... token is expired` (fp 0419ee46c4, user_id=32).
 *      The existing cross-identity guard only blocks a DIFFERENT user's
 *      leftover token; a SAME-user dead token still got sent every time.
 *      A tombstone shared by every refresher breaks the loop at the source:
 *      the first 401 records the token; every later attempt (any refresher,
 *      any tab, after any reload) refuses to send it and the session can
 *      recover/sign-out cleanly instead of 401-looping.
 * FLOW:
 *   ┌───────────┐   ┌──────────────┐   ┌────────────────────────────┐
 *   │ rawToken  │ → │ stableId()   │ → │ mark dead / is dead?        │
 *   │ (JWT str) │   │ jti else hash│   │ (capped localStorage set)   │
 *   └───────────┘   └──────────────┘   └────────────────────────────┘
 * DEPENDENCIES:
 *   - localStorage: durable across the 60s SessionManager tick, axios
 *     retries, and full page reloads (sessionStorage would reset per tab
 *     and re-open the loop on reload).
 * RELATED:
 *   - session-manager/tokenRefresh.ts: marks dead on 401/403, refuses dead.
 *   - src/reducers/axiosSetup.ts + src/reducers/apiClient.ts: same guards.
 */

/** localStorage key holding the JSON array of dead refresh-token ids. */
export const DEAD_REFRESH_STORAGE_KEY = 'quotech_dead_refresh_ids'

/**
 * Cap so a long-lived browser can't grow this unbounded. FIFO — oldest
 * ids drop first. 40 covers many login/logout cycles; a recycled id is
 * astronomically unlikely (ids are jti / content hashes).
 */
export const DEAD_REFRESH_MAX_ENTRIES = 40

/** FNV-1a 32-bit hash → 8-char hex. Deterministic, sync, no crypto needed. */
function fnv1aHex(input: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    // 32-bit FNV prime multiply via shifts to stay in int32 range.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}

/**
 * Stable id for a raw refresh token. Prefers the JWT `jti` claim (the
 * server's own token id, what blacklisting keys on) and falls back to a
 * content hash for non-JWT / malformed strings. Returns null for empty
 * input so callers can no-op safely.
 */
export function refreshTokenId(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null
  const parts = raw.split('.')
  if (parts.length === 3) {
    try {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
      if (payload && typeof payload.jti === 'string' && payload.jti.length > 0) {
        return `jti:${payload.jti}`
      }
    } catch {
      // fall through to content hash
    }
  }
  return `h:${fnv1aHex(raw)}`
}

function readIds(): string[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(DEAD_REFRESH_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function writeIds(ids: string[]): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(DEAD_REFRESH_STORAGE_KEY, JSON.stringify(ids))
  } catch {
    // private-mode / quota — best effort; the cross-identity guard and the
    // server's own 401 still apply, this is purely a loop-breaker optimisation.
  }
}

/**
 * Record that a refresh token was definitively rejected by the server.
 * Idempotent; keeps insertion order and caps to DEAD_REFRESH_MAX_ENTRIES
 * (oldest dropped first).
 */
export function markRefreshTokenDead(raw: string | null | undefined): void {
  const id = refreshTokenId(raw)
  if (!id) return
  const ids = readIds()
  if (ids.includes(id)) return
  ids.push(id)
  if (ids.length > DEAD_REFRESH_MAX_ENTRIES) {
    ids.splice(0, ids.length - DEAD_REFRESH_MAX_ENTRIES)
  }
  writeIds(ids)
}

/** True if this exact refresh token has already been rejected by the server. */
export function isRefreshTokenDead(raw: string | null | undefined): boolean {
  const id = refreshTokenId(raw)
  if (!id) return false
  return readIds().includes(id)
}

/** Wipe the registry (tests / explicit reset). Not needed on normal login —
 *  a fresh login gets a brand-new token whose id won't collide. */
export function clearDeadRefreshRegistry(): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(DEAD_REFRESH_STORAGE_KEY)
  } catch {
    // ignore
  }
}
